import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Button, Paper, Typography, Avatar } from '@mui/material';
import { useSocket } from '../context/SocketProvider';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { keyframes } from '@emotion/react';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { QRCodeCanvas } from 'qrcode.react';
import ReactMarkdown from 'react-markdown';
import { isMobile } from 'react-device-detect';
import { useTheme } from '@mui/material/styles';
import MicIcon from '@mui/icons-material/Mic';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const typingAnimation = keyframes`
  0% { opacity: 0.2; }
  20% { opacity: 1; }
  100% { opacity: 0.2; }
`;

const removeFramedText = (text) => 
	text ? text.replace(/[\r\n]+/g, ' ').replace(/【.*?】/g, '').trim() : '';

const ChatComponent = ({ sessionId: propSessionId }) => {
  const theme = useTheme();
  const [message, setMessage] = useState('');
  const { messages, sendMessage, sessionId, initSession } = useSocket();
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  
  const chatHistoryRef = useRef(null);

  useEffect(() => {
    if (messages.length > 0 && messages[messages.length - 1].type === 'inbound') {
      setIsTyping(false);
    }
    chatHistoryRef.current && (chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight);
  }, [messages]);

  // Initialize session when chat opens for the first time.
  useEffect(() => {
    if (isOpen && !sessionId) {
      initSession();
    }
  }, [isOpen, sessionId, initSession]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = () => {
    if (message.trim()) {
      sendMessage(message, sessionId);
      setMessage('');
      setIsTyping(true);
      document.getElementById('message-input').value = '';
      document.getElementById('message-input').rows = 1;
      chatHistoryRef.current && (chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight);
    }
  };

  const handleInputFocus = () => setIsInputFocused(true);
  const handleInputBlur = () => setIsInputFocused(false);

  return (
    <Box sx={{ position: 'fixed', bottom: 0, right: 0, p: 2, zIndex: 1000 }}>
      {!isOpen && (
        <Button onClick={() => setIsOpen(true)} size="meduim" sx={{ backgroundColor: '#2c3e50', color: 'white' }}>
          <ChatIcon fontSize="large" />
        </Button>
      )}
      {isOpen && (
        <Paper
          elevation={3}
          sx={{
            p: 2,
            maxHeight: { xs: '90vh', md: '80vh' },
            width: 300,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'background.paper',
            border: `${theme.customBorderWeight} solid ${theme.palette.primary.main}`,
            borderRadius: theme.shape.borderRadius,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              height: 56,
              p: 1,
              backgroundColor: '#white',
              borderBottom: '1px solid #e0e0e0',
              zIndex: 1,
            }}
          >
            <Avatar sx={{ mr: 2 }} src="/images/chat.jpg" />
            <Box>
              <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>IVA</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>AI Sales Manager</Typography>
            </Box>
            <Box sx={{ marginLeft: 'auto', position: 'absolute', top: 4, right: 0 }}>
              <Button onClick={() => setShowQRCode(!showQRCode)} size="small" sx={{ color: theme.palette.primary.main }}>
                <QrCodeIcon />
              </Button>
              <Button onClick={() => setIsOpen(false)} size="small" sx={{ color: theme.palette.primary.main }}>
                <CloseIcon />
              </Button>
            </Box>
          </Box>
          <Box ref={chatHistoryRef} sx={{ flex: 1, overflowY: 'auto', pt: 1 }}>
            {messages.map((msg, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: msg.type === 'inbound' ? 'flex-start' : 'flex-end',
                  mb: msg.type === 'inbound' ? 0 : 0, // reduced margin for outbound messages
                  animation: `${fadeIn} 0.5s ease-in-out`
                }}
              >
                <Box
                  sx={{
                    backgroundColor: msg.type === 'inbound' ? theme.palette.background.paper : '#2c3e50',
                    color: msg.type === 'inbound' ? 'text.primary' : 'common.white',
                    borderRadius: 1,
                    p: msg.type === 'inbound' ? 1 : 0.5, // adjusted padding for outbound messages
                    maxWidth: '80%'
                  }}
                >
                  <ReactMarkdown
                    components={{
                      p: ({node, ...props}) => <span {...props} />
                    }}
                  >
                    {removeFramedText(msg.content)}
                  </ReactMarkdown>                
                </Box>
              </Box>
            ))}
            {isTyping && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1, animation: `${typingAnimation} 1s infinite` }}>
                <Typography sx={{ backgroundColor: '#e0e0e0', color: 'text.primary', borderRadius: 1, p: 1 }}>
                  typing...
                </Typography>
              </Box>
            )}
          </Box>
          {showQRCode && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
              <QRCodeCanvas value={`${process.env.REACT_APP_FRONT_END_URL}/voiceClient/${sessionId}`} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                <a href={`${process.env.REACT_APP_FRONT_END_URL}/voiceClient/${sessionId}`} target="_blank" rel="noopener noreferrer">
                  {`${process.env.REACT_APP_FRONT_END_URL}/voiceClient/${sessionId}`}
                </a>
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', mt: 1 }}>
            <TextField
              id="message-input"
              fullWidth
              variant="outlined"
              size="small"
              multiline
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              sx={{
                backgroundColor: '#DFDFF7',
                borderRadius: theme.shape.borderRadius,
                '& .MuiOutlinedInput-root': {
                  borderRadius: theme.shape.borderRadius,
                  '& fieldset': { borderColor: '#2c3e50' },
                  '&:hover fieldset': { borderColor: '#2c3e50' },
                  '&.Mui-focused fieldset': { borderColor: '#2c3e50' },
                  '& .MuiInputBase-input': { color: theme.customInputText },
                },
              }}
            />
            {isMobile && !isInputFocused && (
              <Button
                variant="contained"
                onClick={() => alert('Audio functionality not implemented')}
                sx={{
                  backgroundColor: '#D7D6FF',
                  minWidth: '40px',
                  mr: 1,
                }}
              >
                <MicIcon sx={{ color: theme.palette.primary.main }}/>
              </Button>
            )}
            <Button variant="contained" sx={{ backgroundColor: '#2c3e50', minWidth: 'auto' }} onClick={handleSendMessage} size="small">
              <SendIcon sx={{ color: theme.palette.primary.main }}/> 
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default ChatComponent;